import React, { Fragment, useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import 'url-search-params-polyfill';
import {
  PageHeader,
  SimpleDataTable,
  Section,
  makeBreadcrumb,
  getObjByValue,
  slugify,
  findObjIndexByKey,
  GatsbyLink,
  TabContainer,
  Tab
} from '@budget-monorepo/shared-ui';

import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';
import PlanFilter from '../components/PlanFilter';

const SummaryItem = (props) => {
  const { data, location } = props;
  const { content, site, tabsNav } = data;
  const tabs = tabsNav.navItems;
  const { parent, fields } = content
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = parent.parent;
  const path = location.pathname;
  const pathPrefix = process.env.GATSBY_PATH_PREFIX;
  const relPath = path.replace(pathPrefix, '');
  const { pageTitle, breadcrumbprops } = makeBreadcrumb({
    href: fields.slug,
    text: fields.breadcrumbTitle
  });
  const title = `Capital Investment ${fields.breadcrumbTitle}`;
  const layoutProps = {
    activeIndex: 1,
    pre: () => <PlanFilter path={relPath} />,
    above: () => <PageHeader title={title} />,
    pageTitle,
    breadcrumbprops,
    buildTimeStamp: site.buildTime,
    dataTimeStamp: modifiedTime,
    containerClasses: 'main-content main-content--two'
  };

  const createTableData = (tabData) => {
    const { data: sumData, ...tableRest } = tabData;
    const newTableData = sumData.map((row) => {
      const { summaryTableBase, fields } = content;
      // If a display data field exist, use that to render the link text
      const summaryTableDisplay = `${summaryTableBase}Display`;
      const text = row[summaryTableDisplay] || row[summaryTableBase];
      const href = `${fields.slug}${slugify(row[summaryTableBase])}`;
      const replaceKey = row[summaryTableDisplay] ? summaryTableDisplay : summaryTableBase;
      return Object.assign({}, row, {
        [replaceKey]: <GatsbyLink to={href}>{text}</GatsbyLink>
      });
    });
    const tableProps = { data: newTableData, ...tableRest };
    return tableProps;
  } 

  const urlToTabIndex = (location) => {
    let index = 0;
    if (!location.href) {
      return null;
    }
    const url = new URL(location.href);
    const params = new URLSearchParams(url.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      // Tab text set in tabs.json
      const tabKey = 'href'; 
      const tabData = tabs;
      index = findObjIndexByKey({ arr: tabData, value:tabParam, key: tabKey, slug: true });
    }
    return index;
  };

  const urlToSubTabIndex = (location) => {
    let index = 0;
    if (!location.href) {
      return null;
    }
    const url = new URL(location.href);
    const params = new URLSearchParams(url.search);
    const tabParam = params.get('tab');
    const subTabParam = params.get('subtab');
    if (tabParam && subTabParam) {
      // Tab text set in tabs.json
      const tabKey = 'href'; 
      const subTabKey = 'name';
      const parentTab = getObjByValue(tabs, tabParam, tabKey);
      const subTabData = content[parentTab.dataKey];
      index = findObjIndexByKey({ arr: subTabData, value: subTabParam, key: subTabKey, slug: true});
    }
    return index;
  };

  const [ tabIndex, setTabIndex ] = useState(urlToTabIndex(location));
  const [ subTabIndex, setSubTabIndex ] = useState(urlToSubTabIndex(location));


  const onTabChange = (tabRef, curLocation) => {
    const { location: newLocation } = props;
    if (global.window && curLocation.href) {
      const tab = tabRef.current.innerText;
      const { pathname } = newLocation;
      const url = `${pathname}?tab=${slugify(tab)}`;
      // eslint-disable-next-line no-undef
      window.history.replaceState({}, '', url);
      const index = findObjIndexByKey({ arr: tabs, value: tab, key: 'text'});
      setTabIndex(index);
      // reset sub tab on tab change
      setSubTabIndex(0);
    }
  };

  const onSubTabChange = ({tabRef, location: curLocation, parentTab}) => {
    const { location } = props;
    if (global.window && curLocation.href && tabRef.current) {
      const subTab = tabRef.current.innerText;
      const { pathname } = location;
      const url = `${pathname}?tab=${parentTab.href}&subtab=${slugify(subTab)}`;
      // eslint-disable-next-line no-undef
      window.history.replaceState({}, '', url);
      const index = findObjIndexByKey({ arr: content[parentTab.dataKey], value: subTab, key: 'name'});
      setSubTabIndex(index);
    }
  };




  // metadata for the page
  const metaTitle = title;
  const metaDescription = `${title} summary table.`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        <Section>
          {tabs
          && (
          
          <TabContainer
            defaultTab={tabIndex}
            onTabChange={(tabRef) => onTabChange(tabRef, location)}
          >
            {
              tabs.map((tab) => {
                return(
                  <Tab 
                    title={tab.text}
                    key={`tab-${tab.href}`}
                  >
                    <TabContainer
                      nested
                      defaultTab={subTabIndex}
                      onTabChange={(tabRef) => onSubTabChange({ tabRef, location, parentTab: tab })}
                    >
                      {
                        content[tab.dataKey].map((subTab) => (
                          <Tab
                            title={subTab.name}
                            key={`subtab-${subTab.value}`}
                          >
                            <SimpleDataTable title={title} table={createTableData(subTab)} maxColWidth={375} download={`${location.pathname}/${subTab.name}`} />
                          </Tab>
                        ) )
                      }
                    </TabContainer>
                  </Tab>
                  )
                }
              )
            }
          </TabContainer>
          )
          }
        </Section>
      </Layout>
    </Fragment>
  );
};

SummaryItem.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object
};

export default SummaryItem;

export const query = graphql`
  query($slug: String!) {
    site {
      ...SiteMetadata
    }
    tabsNav: navJson(navType: {eq: "tabs"}) {
      ...SiteNav
    }
    content: summaryItem(fields: { slug: { eq: $slug } }) {
      title
      budgetSection
      summaryTableBase
      fields {
        slug
        breadcrumbTitle
      }
      byYearTab {
        name
        value
        headers {
          name
          type
          align
          value
        }
        data {
          govLevelOneDisplay
          govLevelOne
          capitalAgency
          policyArea
          stateBondCap
          nonBondCap
          operatingFunds
          federalFunds
          otherFunds
          total
        }
        total {
          govLevelOneDisplay
          policyArea
          capitalAgency
          stateBondCap
          nonBondCap
          operatingFunds
          federalFunds
          otherFunds
          total
        }
      }
      bySourceTab {
        name
        value
        headers {
          name
          type
          align
          value
        }
        data {
          govLevelOneDisplay
          govLevelOne
          capitalAgency
          policyArea
          fy1
          fy2
          fy3
          fy4
          fy5
          fy6
        }
        total {
          govLevelOneDisplay
          capitalAgency
          policyArea
          fy1
          fy2
          fy3
          fy4
          fy5
          fy6
        }
      }
      # Get the last modified timestamp for the file powering the plan item pages
      ...SummaryItemDataTimeStamp
    }
  }
`;
